import {
	ChangeEvent,
	useState,
	useCallback,
	useEffect,
	Dispatch,
	SetStateAction
} from 'react';
import { Input } from '@fluentui/react-components';
import useDebouncer from '../Hooks/useDebouncer';

export interface SearchbarProps<T> {
	/** Array of the data that is to be searched over. */
	searchableData: T[];
	/** Dispatch event to update the data  */
	setSearchableData: Dispatch<SetStateAction<T[]>>;
}

export default function Searchbar<T>(props: SearchbarProps<T>) {
	// used for searching against dates
	const reISO = /^(\d{4})-(\d{2})-(\d{2})$/;

	const [searchText, setSearchText] = useState('');
	const { searchableData, setSearchableData } = props;

	useEffect(() => {
		setSearchableData(searchableData);
	}, [setSearchableData, searchableData]);

	const searchTextChanged = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			setSearchText(event.target.value);
		},
		[]
	);

	useDebouncer(
		() => {
			function filterByValue(data: T[], searchValue: string) {
				// Dont initiate search until we have enough to use
				if (searchValue.length >= 3) {
					return data.filter((obj) => {
						if (obj && typeof obj === 'object') {
							const searchableObject = obj as Record<
								string,
								unknown
							>;
							Object.keys(searchableObject).some((key) => {
								let searchableValue;
								if (typeof searchableObject[key] === 'string') {
									if (reISO.exec(searchableObject[key])) {
										// Key was a date
										searchableValue = new Date(
											searchableObject[key] + '00:00:00'
										).toLocaleDateString('en-US');
									} else {
										searchableValue = searchableObject[key];
									}
								} else {
									// Cast as string
									searchableValue = String(
										searchableObject[key]
									);
								}

								return searchableValue
									.toLowerCase()
									.includes(searchValue.toLowerCase());
							});
						}

						return obj;
					});
				}

				return data;
			}

			props.setSearchableData(
				filterByValue(props.searchableData, searchText)
			);
		},
		[searchText],
		500
	);

	return (
		<Input
			type='text'
			value={searchText}
			onChange={searchTextChanged}
			placeholder='Search...'
			style={{ width: '100%' }}
			appearance='underline'
		/>
	);
}
